// src/app/core/idle.service.ts
import { Injectable, NgZone } from '@angular/core';
import { fromEvent, merge, Subject, timer } from 'rxjs';
import { switchMap, startWith, tap, throttleTime } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from './auth.service';

const LS_LAST_ACTIVITY = 'lastActivityUtc';
const LS_FORCE_LOGOUT = 'forceLogout';
const INACTIVITY_MS = 20 * 60 * 1000;
const WARN_MS = 19 * 60 * 1000;
const BUMP_THROTTLE_MS = 1000; // Throttle bump calls to max once per second

@Injectable({ providedIn: 'root' })
export class IdleService {
    private activity$ = new Subject<void>();
    private bc?: BroadcastChannel;
    private warningDialogRef: any = null;
    private isActive = false;
    private events$: any;
    private subscriptions: any[] = [];
    private lastBumpTime = 0;
    private lastWrittenValue: string | null = null;
    private storageEventListener: ((e: StorageEvent) => void) | null = null;

    constructor(
        private zone: NgZone,
        private dialog: MatDialog,
        private authService: AuthService
    ) {
        // Initialize but don't start monitoring yet
        this.initializeBroadcastChannel();

        // Listen to auth state changes
        this.authService.currentUser$.subscribe(user => {
            if (user) {
                this.startMonitoring();
            } else {
                this.stopMonitoring();
            }
        });
    }

    private initializeBroadcastChannel() {
        // Don't re-initialize if already set up
        if (this.storageEventListener) {
            return;
        }

        // multi-tab sync via storage event
        // CRITICAL: Only update activity$ when receiving storage events, don't write back to localStorage
        this.storageEventListener = (e: StorageEvent) => {
            if (this.isActive) {
                if (e.key === LS_LAST_ACTIVITY && e.newValue) {
                    // Only update activity if the value actually changed and came from another tab
                    if (e.newValue !== this.lastWrittenValue) {
                        this.lastWrittenValue = e.newValue;
                        // Just reset the timer, don't write to localStorage again
                        this.activity$.next();
                    }
                }
                if (e.key === LS_FORCE_LOGOUT && e.newValue === '1') {
                    this.onTimeout();
                }
            }
        };
        window.addEventListener('storage', this.storageEventListener);

        // optional: BroadcastChannel for faster sync
        if (!this.bc) {
            try {
                this.bc = new BroadcastChannel('idle');
                this.bc.onmessage = (msg) => {
                    if (this.isActive) {
                        if (msg.data === 'bump') {
                            // Just reset the timer, don't write to localStorage again
                            this.activity$.next();
                        }
                        if (msg.data === 'logout') {
                            this.onTimeout();
                        }
                    }
                };
            } catch { /* unsupported */ }
        }
    }

    private startMonitoring() {
        if (this.isActive) return; // Already active

        this.isActive = true;
        this.lastBumpTime = 0;

        // Re-initialize BroadcastChannel and storage listener if they were removed
        if (!this.storageEventListener) {
            this.initializeBroadcastChannel();
        }

        // init last activity
        const currentValue = Date.now().toString();
        if (!localStorage.getItem(LS_LAST_ACTIVITY)) {
            localStorage.setItem(LS_LAST_ACTIVITY, currentValue);
            this.lastWrittenValue = currentValue;
        } else {
            this.lastWrittenValue = localStorage.getItem(LS_LAST_ACTIVITY);
        }

        // Setup user activity events with throttling to prevent excessive calls
        this.events$ = merge(
            fromEvent(document, 'mousemove'),
            fromEvent(document, 'keydown'),
            fromEvent(document, 'click'),
            fromEvent(document, 'scroll'),
            fromEvent(document, 'touchstart'),
            fromEvent(document, 'visibilitychange')
        );

        // Throttle event subscriptions to prevent excessive bump calls
        const eventSubscription = this.events$.pipe(
            throttleTime(BUMP_THROTTLE_MS)
        ).subscribe(() => this.bump('event'));

        // Setup timers
        this.zone.runOutsideAngular(() => {
            const warnSubscription = this.activity$.pipe(
                startWith(void 0),
                switchMap(() => timer(WARN_MS)),
                tap(() => this.zone.run(() => this.onWarn()))
            ).subscribe();

            const timeoutSubscription = this.activity$.pipe(
                startWith(void 0),
                switchMap(() => timer(INACTIVITY_MS)),
                tap(() => this.zone.run(() => this.onTimeout()))
            ).subscribe();

            // Store subscriptions for cleanup
            this.subscriptions = [eventSubscription, warnSubscription, timeoutSubscription];
        });
    }

    private stopMonitoring() {
        if (!this.isActive) return; // Already inactive

        this.isActive = false;
        this.lastBumpTime = 0;
        this.lastWrittenValue = null;

        // Close any open warning dialog
        if (this.warningDialogRef) {
            this.warningDialogRef.close();
            this.warningDialogRef = null;
        }

        // Clean up subscriptions
        if (this.subscriptions) {
            this.subscriptions.forEach(sub => sub.unsubscribe());
            this.subscriptions = [];
        }

        // Remove storage event listener
        if (this.storageEventListener) {
            window.removeEventListener('storage', this.storageEventListener);
            this.storageEventListener = null;
        }

        // Close BroadcastChannel
        if (this.bc) {
            this.bc.close();
            this.bc = undefined;
        }

        // Clear localStorage items
        localStorage.removeItem(LS_LAST_ACTIVITY);
        localStorage.removeItem(LS_FORCE_LOGOUT);
    }

    bump(origin?: string) {
        if (!this.isActive) return; // Only bump when monitoring is active

        // Throttle: Only write to localStorage if enough time has passed
        const now = Date.now();
        if (now - this.lastBumpTime < BUMP_THROTTLE_MS) {
            // Still update activity$ to reset timers, but skip localStorage write
            this.activity$.next();
            return;
        }

        this.lastBumpTime = now;
        const newValue = now.toString();
        
        // Only write if value actually changed (prevents unnecessary writes)
        if (newValue !== this.lastWrittenValue) {
            this.lastWrittenValue = newValue;
            localStorage.setItem(LS_LAST_ACTIVITY, newValue);
            // Only broadcast if we actually wrote (not from storage/bc events)
            if (origin === 'event' || origin === 'user-extend') {
                this.bc?.postMessage('bump');
            }
        }
        
        this.activity$.next();
    }

    onWarn() {
        if (!this.isActive) return; // Only warn when monitoring is active

        // Only show warning dialog if no dialog is currently open
        if (!this.warningDialogRef) {
            // Dynamic import to avoid circular dependency
            import('../../shared/components/idle-warning-dialog/idle-warning-dialog.component')
                .then(({ IdleWarningDialogComponent }) => {
                    this.warningDialogRef = this.dialog.open(IdleWarningDialogComponent, {
                        disableClose: true,
                        width: '400px',
                        data: {}
                    });

                    this.warningDialogRef.afterClosed().subscribe((result: string) => {
                        this.warningDialogRef = null;

                        if (result === 'extend') {
                            // User chose to continue - reset timer
                            this.bump('user-extend');
                        } else if (result === 'logout') {
                            // User chose to logout immediately
                            this.onTimeout();
                        } else if (result === 'timeout') {
                            // Countdown expired - automatic logout
                            this.onTimeout();
                        }
                    });
                })
                .catch(error => {
                    console.error('Error loading idle warning dialog:', error);
                    // Fallback: automatic logout if dialog can't be displayed
                    this.onTimeout();
                });
        }
    }

    onTimeout() {
        if (!this.isActive) return; // Only timeout when monitoring is active

        // Close warning dialog if it's open
        if (this.warningDialogRef) {
            this.warningDialogRef.close();
            this.warningDialogRef = null;
        }

        // Set flag so other tabs know they need to logout
        localStorage.setItem(LS_FORCE_LOGOUT, '1');
        this.bc?.postMessage('logout');

        // Perform logout (this will trigger stopMonitoring via auth state change)
        this.authService.logout();

        // Clear force logout flag after a moment
        setTimeout(() => {
            localStorage.removeItem(LS_FORCE_LOGOUT);
        }, 1000);
    }
}
